import { Text } from '@atlaskit/primitives';
import { useCallback, useEffect, useState } from 'react';

import authRequest from '../../../../../utils/fetch';
import { ListToggle } from '../../../components/list-toggle';
import type { CodeReviewerSettingValue } from '../../types';

const setUrl = '/gateway/api/devai-core/public/v2/settings/set';

type CodeReviewerSettingToggleProps = {
	type: 'repository' | 'workspace';
	entityUrl: string;
	setting: boolean;
	disableToggle: boolean;
	onToggle?: (newValue: boolean) => void;
};
export const CodeReviewerSettingToggle = ({
	type,
	entityUrl,
	setting,
	disableToggle,
	onToggle,
}: CodeReviewerSettingToggleProps) => {
	const [isEnabled, setIsEnabled] = useState<boolean>(setting);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setIsEnabled(setting);
	}, [setting]);

	const getSettingValue = useCallback(
		(newValue: boolean): CodeReviewerSettingValue => {
			switch (type) {
				case 'repository':
					return { autoreview_enabled: newValue };
				case 'workspace':
					return { autoreview_activated: newValue };
				default:
					throw new Error('An error has occured');
			}
		},
		[type],
	);

	const updateSetting = useCallback(async () => {
		setError(null);
		// Optimistic Update
		const newValue = !isEnabled;
		setIsEnabled(newValue);

		if (onToggle) {
			onToggle(newValue);
		}

		try {
			const res = await fetch(
				authRequest(setUrl, {
					method: 'POST',
					body: JSON.stringify({
						settings: [
							{
								containerType: type,
								containerId: entityUrl,
								key: 'autoreview_settings',
								value: getSettingValue(newValue),
							},
						],
					}),
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}),
			);

			if (!res.ok) {
				throw new Error(`http error status ${res.status} with text: ${res.statusText}`);
			}
		} catch (e) {
			setError('An error has occured');
			// reverse optimistic update
			setIsEnabled(!newValue);
		}
	}, [entityUrl, getSettingValue, isEnabled, onToggle, type]);

	if (error) {
		return <Text>{error}</Text>;
	}

	return (
		<ListToggle
			onChange={updateSetting}
			isChecked={isEnabled}
			analyticsEvent={{
				action: 'clicked',
				actionSubject: 'code-reviewer-setting-toggle',
				actionSubjectId: type === 'repository' ? 'repository-toggle' : 'organisation-toggle',
				source:
					type === 'repository'
						? 'code-reviewer-repository-settings'
						: 'code-reviewer-organisation-settings',
			}}
			isDisabled={disableToggle}
			toggleType={type === 'repository' ? 'repository' : 'organization'}
		/>
	);
};
